import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  attachDocumentsFailed,
  attachDocumentsRequest,
  attachDocumentsSuccess,
  deleteDocumentsFailed,
  deleteDocumentsRequest,
  deleteDocumentsSuccess,
  documentsFailed,
  documentsRequest,
  documentsSuccess,
} from './registration.actions';
import { GraphQlService } from '@shared/services/graphql-service';
import { catchError, exhaustMap, map, of, switchMap } from 'rxjs';
import {
  DELETE_DOCUMENTS,
  GET_DOCUMENTS,
} from '../omc-registration/graphql/omc-registration.graphql';
import { DocumentResponse } from '../omc-registration/responses/omc-documents';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { ATTACH_DOCUMENTS } from '@modules/company-profile/graphql/company-profile.graphql';
import { AttachCompanyDocumentResponse } from '@modules/company-profile/responses/attach-documents.response';
import { DeleteAttachmentResponse } from '../omc-registration/responses/delete-attachment.response';
import { ErrorCodes } from '@shared/enums/custom-error-code';

@Injectable({
  providedIn: 'root',
})
export class RegistrationEffects {
  private actions$ = inject(Actions);
  private graphQlService = inject(GraphQlService);

  getDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(documentsRequest),
      switchMap(({ request }) =>
        this.graphQlService
          .query<DocumentResponse>({
            query: GET_DOCUMENTS,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              registrationType: request.registrationType,
              companyType: request.companyType,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data.getAttachmentTypesByRegistrationType;
              if (!payload) {
                return documentsFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed',
                    data: null,
                  },
                });
              }
              return documentsSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Success',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                documentsFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                })
              )
            )
          )
      )
    )
  );

  submitDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(attachDocumentsRequest),
      exhaustMap(({ request }) =>
        this.graphQlService
          .mutate<AttachCompanyDocumentResponse>({
            mutation: ATTACH_DOCUMENTS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const gql = apolloResult.data?.uploadAttachment;

              if (!gql || !gql.status || !gql.data) {
                return attachDocumentsFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: gql?.message ?? 'Attach document failed',
                    data: null,
                  },
                });
              }

              return attachDocumentsSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: gql.message,
                  data: gql.data,
                },
              });
            }),

            catchError((err) =>
              of(
                attachDocumentsFailed({
                  error: {
                    success: false,
                    statusCode: 500,
                    message: err?.message ?? 'Network error',
                    data: null,
                  },
                })
              )
            )
          )
      )
    )
  );

  deleteDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteDocumentsRequest),
      exhaustMap(({ request }) =>
        this.graphQlService
          .mutate<DeleteAttachmentResponse>({
            mutation: DELETE_DOCUMENTS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { attachmentUuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const gql = apolloResult.data?.deleteAttachment;
              //console.log("deleteAttachment 0001::", gql);
              if (!gql.status || gql.code !== ErrorCodes.SUCCESS) {
                return deleteDocumentsFailed({
                  error: {
                    success: false,
                    statusCode: 500,
                    message: gql?.message ?? 'delete attachment failed',
                    data: null,
                  },
                });
              }
              return deleteDocumentsSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: gql.message,
                  data: gql.data,
                },
              });
            }),
            catchError((err) =>
              of(
                deleteDocumentsFailed({
                  error: {
                    success: false,
                    statusCode: 500,
                    message: err?.message ?? 'Network error',
                    data: null,
                  },
                })
              )
            )
          )
      )
    )
  );
}
